import { Select } from '@thanx/uikit/select'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { LiveUpdatesConfiguration } from 'api/liveUpdates.types'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('live_updates.picker')

// The Overview preview's fixed-width select (Sketch prototype v40
// `.cfgselwrap`: 420px, capped at 60% of the row).
const FIXED_SELECT_WIDTH = 420
const FIXED_SELECT_MAX_SHARE = '60%'

// The backend `label` is just the provider join ("Olo + DoorDash", "Flybuy") —
// the location-count suffix is intentionally a client-side concern (backend
// briefly assembled it server-side, then reverted to avoid double-counting).
export const pickerOptionLabel = (
  configuration: LiveUpdatesConfiguration
): string =>
  configuration.locationsCount === 1
    ? t('option_one_location', { label: configuration.label })
    : t('option', {
        label: configuration.label,
        count: configuration.locationsCount,
      })

export interface ConfigurationPickerProps {
  configurations: LiveUpdatesConfiguration[]
  selectedId: string | null
  onSelectConfiguration: (selected: { value: string }[]) => void
  /** Both tabs pick the same configuration, but frame it differently —
   * Configuration sets one up, Overview previews one. */
  labelKey: 'label' | 'preview_label'
  ariaLabelKey: 'aria_label' | 'preview_aria_label'
  disabled?: boolean
  /** Sizes the select to the prototype's fixed width and pushes `children`
   * to the far end of the row, rather than letting the select fill it. */
  fixedWidth?: boolean
  /** The control that sits inline to the right of the select — "View
   * locations" on Configuration, the read-only note on Overview. */
  children?: React.ReactNode
}

// The one "choose a configuration" control, shared by the Configuration tab
// (where it drives the editor) and the Overview tab's setup preview (Sketch
// prototype live-updates-onboarding v34, `#cfgdd`: "In prod this is the same
// control the config page already has").
const ConfigurationPicker = ({
  configurations,
  selectedId,
  onSelectConfiguration,
  labelKey,
  ariaLabelKey,
  disabled,
  fixedWidth = false,
  children,
}: ConfigurationPickerProps) => {
  const [css] = useStyletron()

  const options = configurations.map(configuration => ({
    value: configuration.id,
    label: pickerOptionLabel(configuration),
  }))

  return (
    <div>
      <Text.BodyText4
        bold
        color="grey90"
        className={css({ display: 'block', marginBottom: '8px' })}
      >
        {t(labelKey)}
      </Text.BodyText4>
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        })}
      >
        <div
          data-testid="lu-configuration-picker-select"
          className={css(
            fixedWidth
              ? {
                  flex: `0 1 ${FIXED_SELECT_WIDTH}px`,
                  maxWidth: FIXED_SELECT_MAX_SHARE,
                }
              : { flex: '1 1 320px' }
          )}
        >
          <Select
            aria-label={t(ariaLabelKey)}
            options={options}
            value={options.filter(option => option.value === selectedId)}
            onChange={onSelectConfiguration}
            clearable={false}
            searchable={false}
            disabled={disabled}
          />
        </div>
        {fixedWidth && children ? (
          <div className={css({ marginLeft: 'auto' })}>{children}</div>
        ) : (
          children
        )}
      </div>
    </div>
  )
}

export default ConfigurationPicker
